import { z } from "zod";
import { FermentationAlertKindEnum, FermentationAlertLimitModeEnum, } from "@brewbase/db/types";
import { splitTelemetryRole } from "./telemetry-attributes";
/**
 * Live alerting: what a rule means, and whether it is currently tripped.
 *
 * Everything here is a pure function of a rule and a reading. The watcher owns
 * reading the database and calling out; this module owns the judgement, so the
 * part that decides whether to wake someone at 3am is testable without a node,
 * a database, or a phone.
 *
 * ## The four kinds
 *
 * `NODE_OFFLINE`  — the node is not holding its downlink, or is holding it but
 *                   has stopped pushing. Both are "we are flying blind", which
 *                   is one thing to a person being woken up, so they are one
 *                   alert with two causes rather than two alerts.
 * `INPUT_MISSING` — a named input has had no *readable* value recently. Readable
 *                   matters: a dead probe usually keeps reporting on schedule
 *                   and writes NaN, which is how a node says "I could not read
 *                   this scan". Checking only for absent points would miss
 *                   exactly the case the alert exists for.
 * `DEVICE_FAULT`  — a device on the node reports a non-empty error string.
 * `PROCESS_LIMIT` — a measured value sat outside its band.
 *
 * ## Durations and hysteresis
 *
 * A rule trips only once its condition has held continuously for `durationS`,
 * and clears only once the condition has been false continuously for
 * `CLEAR_HOLD_S`. The asymmetry is deliberate: waiting five minutes before
 * waking someone is prudence, but making them wait five more minutes to watch
 * a recovered value clear is just noise. The clear hold still exists so a value
 * oscillating on the band edge does not flap the alert open and shut.
 */
// =============================================================================
// CONSTANTS
// =============================================================================
/** How long a condition must read false continuously before its alert clears. */
export const CLEAR_HOLD_S = 60;
/**
 * How old the newest sample may be before a signal counts as not reporting.
 *
 * This is a freshness threshold, not a duration: it answers "is this arriving
 * right now", and the rule's own `durationS` then decides how long that must
 * stay false before anyone is woken. Keeping the two separate means each does
 * one job — without it, a rule's duration would be measured from a moment that
 * itself depended on a second, invisible threshold.
 *
 * Comfortably above the node's 1 Hz push and its ten-second batching, so a
 * normal gap between batches never reads as a gap in the data.
 */
export const STALE_AFTER_S = 30;
/** Default duration for a new rule, matching the column default. */
export const DEFAULT_ALERT_DURATION_S = 300;
/**
 * How long after process start the node-liveness rules stay quiet.
 *
 * Node liveness is a held connection in process memory, so every deploy starts
 * with every node "offline" until it redials. Without this, shipping a change
 * pages the whole on-call list every time.
 */
export const STARTUP_GRACE_S = 180;
/**
 * Beyond this many simultaneously notifiable alerts, the sweep sends one digest
 * instead of one message each.
 *
 * The per-condition hysteresis above is the real defence against noise; this is
 * the last-resort backstop for the correlated failures it cannot see — the
 * tailnet dropping, or BrewBase's own host losing the network, either of which
 * makes every node look dead at the same instant. Ten alerts arriving at once
 * are one situation, and ten text messages describe it worse than one does.
 */
export const NOTIFICATION_DIGEST_THRESHOLD = 3;
/** How long an unacknowledged alert waits before everyone gets a phone call. */
export const VOICE_ESCALATION_S = 30 * 60;
/** How long an acknowledgement link stays usable. */
export const ACK_TOKEN_TTL_S = 7 * 24 * 60 * 60;
/** Whether a kind watches named signals rather than the node as a whole. */
export function isSignalRule(kind) {
    return (kind === FermentationAlertKindEnum.INPUT_MISSING ||
        kind === FermentationAlertKindEnum.PROCESS_LIMIT);
}
/** Whether a kind carries a list of signals rather than exactly one. */
export function isMultiSignalRule(kind) {
    return kind === FermentationAlertKindEnum.INPUT_MISSING;
}
/**
 * The setpoint a deviation rule measures against.
 *
 * An explicit pick always wins. Otherwise the sibling of the watched signal
 * under the naming grammar: `temperature_pv` measures against
 * `temperature_sp`. A watched signal that carries no role has no derivable
 * sibling and returns null, which the evaluator treats as "cannot judge"
 * rather than as "in band" — a rule that silently never fires is worse than
 * one that visibly cannot be evaluated.
 */
export function setpointAttributeFor(rule) {
    if (rule.setpointAttributeName)
        return rule.setpointAttributeName;
    if (!rule.attributeName)
        return null;
    const { subject, role } = splitTelemetryRole(rule.attributeName);
    if (role === null)
        return null;
    return `${subject}_sp`;
}
const clear = { status: "clear" };
/**
 * Whether a rule's condition holds for one observation.
 *
 * "Unknown" is a real third answer and not a synonym for either of the others.
 * A deviation rule whose setpoint has not been reported cannot say whether the
 * value is in band; treating that as clear hides a fault and treating it as
 * tripped invents one. The caller freezes the timer instead, so a brief gap
 * neither raises nor resets anything.
 */
export function evaluateAlertRule(rule, observation, format = defaultFormatter) {
    switch (rule.kind) {
        case FermentationAlertKindEnum.NODE_OFFLINE:
            return evaluateNodeOffline(observation);
        case FermentationAlertKindEnum.INPUT_MISSING:
            return evaluateInputMissing(rule, observation);
        case FermentationAlertKindEnum.DEVICE_FAULT:
            return evaluateDeviceFault(observation);
        case FermentationAlertKindEnum.PROCESS_LIMIT:
            return evaluateProcessLimit(rule, observation, format);
        default:
            return { status: "unknown", reason: "unrecognised alert kind" };
    }
}
function evaluateNodeOffline(observation) {
    if (observation.nodeConnected === false) {
        return {
            status: "tripped",
            detail: "the node is not connected to BrewBase",
            observedValue: null,
        };
    }
    // Connected but silent is the more dangerous half: the link looks healthy
    // while nothing is being recorded.
    const age = observation.uplinkAgeS;
    if (age === undefined)
        return clear;
    if (age === null) {
        return {
            status: "tripped",
            detail: "the node is connected but has never sent any data",
            observedValue: null,
        };
    }
    return age > STALE_AFTER_S
        ? {
            status: "tripped",
            detail: `the node is connected but has sent no data for ${formatDuration(age)}`,
            observedValue: null,
        }
        : clear;
}
function evaluateInputMissing(rule, observation) {
    // The rule watches a set, so which member this is comes with the reading.
    const name = observation.signalName ?? rule.attributeName ?? "the input";
    // Null age means nothing readable has ever arrived in the window looked at,
    // which is as missing as it gets.
    if (observation.readableAgeS === null) {
        return {
            status: "tripped",
            detail: `${name} has no readable value`,
            observedValue: null,
        };
    }
    if (observation.readableAgeS === undefined) {
        return { status: "unknown", reason: "no reading was taken" };
    }
    return observation.readableAgeS > STALE_AFTER_S
        ? {
            status: "tripped",
            detail: `${name} has had no readable value for ${formatDuration(observation.readableAgeS)}`,
            observedValue: null,
        }
        : clear;
}
function evaluateDeviceFault(observation) {
    const error = observation.deviceError;
    if (error === undefined) {
        return { status: "unknown", reason: "no device snapshot" };
    }
    if (!error)
        return clear;
    // The device is named in the detail because one rule watches several of
    // them: without it, four faulty devices produce four alerts that read
    // identically and name nothing anyone can go and look at.
    return {
        status: "tripped",
        detail: observation.deviceId ? `${observation.deviceId}: ${error}` : error,
        observedValue: null,
    };
}
function evaluateProcessLimit(rule, observation, format) {
    const name = rule.attributeName ?? "the value";
    const value = observation.value;
    if (value === undefined || value === null || !Number.isFinite(value)) {
        // A missing process value is the INPUT_MISSING rule's business. Judging it
        // here too would raise two alerts for one probe, so this rule abstains.
        return { status: "unknown", reason: `${name} has no readable value` };
    }
    if (rule.limitMode === FermentationAlertLimitModeEnum.ABSOLUTE) {
        if (rule.minValue !== null && value < rule.minValue) {
            return {
                status: "tripped",
                detail: `${name} is ${format(value)}, below the limit of ${format(rule.minValue)}`,
                observedValue: value,
            };
        }
        if (rule.maxValue !== null && value > rule.maxValue) {
            return {
                status: "tripped",
                detail: `${name} is ${format(value)}, above the limit of ${format(rule.maxValue)}`,
                observedValue: value,
            };
        }
        return clear;
    }
    if (rule.limitMode === FermentationAlertLimitModeEnum.DEVIATION) {
        const setpointName = setpointAttributeFor(rule);
        if (!setpointName) {
            return {
                status: "unknown",
                reason: `${name} has no setpoint to measure against`,
            };
        }
        const setpoint = observation.setpoint;
        if (setpoint === undefined || setpoint === null || !Number.isFinite(setpoint)) {
            return {
                status: "unknown",
                reason: `${setpointName} has no readable value`,
            };
        }
        const deviation = value - setpoint;
        if (rule.deviationBelow !== null && deviation < -Math.abs(rule.deviationBelow)) {
            return {
                status: "tripped",
                detail: `${name} is ${format(value)}, ${format(Math.abs(deviation))} below its setpoint of ${format(setpoint)}`,
                observedValue: value,
            };
        }
        if (rule.deviationAbove !== null && deviation > Math.abs(rule.deviationAbove)) {
            return {
                status: "tripped",
                detail: `${name} is ${format(value)}, ${format(deviation)} above its setpoint of ${format(setpoint)}`,
                observedValue: value,
            };
        }
        return clear;
    }
    return { status: "unknown", reason: "the rule has no band" };
}
/**
 * Advance a condition timer by one observation.
 *
 * The timer, not the sweep, is what makes "for five minutes" mean five minutes
 * of wall clock rather than five minutes of the sweep happening to run. It
 * lives in the database, so a deploy in the middle of an excursion resumes the
 * count instead of restarting it.
 */
export function advanceTimer(previous, verdict, durationS, now) {
    if (verdict.status === "unknown")
        return { action: "freeze" };
    const isTrue = verdict.status === "tripped";
    // A change of truth value restarts the clock; holding steady keeps it.
    const timer = previous && previous.isTrue === isTrue
        ? previous
        : { isTrue, since: now };
    const heldS = (now.getTime() - timer.since.getTime()) / 1000;
    if (isTrue) {
        return heldS >= durationS
            ? { action: "raise", timer }
            : { action: "hold", timer };
    }
    return heldS >= CLEAR_HOLD_S
        ? { action: "clear", timer }
        : { action: "hold", timer };
}
// =============================================================================
// DEDUPE KEYS
// =============================================================================
/**
 * The identity of a condition, as opposed to an occurrence of it.
 *
 * One open alert exists per key (enforced by a partial unique index), so a
 * sweep running every few seconds cannot stack up duplicates of one excursion,
 * and a condition that clears and returns is a second alert rather than a
 * revival of the first.
 */
export function dedupeKey(parts) {
    return [
        `rule:${parts.groupAlertId}`,
        `ferm:${parts.fermentationId ?? "-"}`,
        `subject:${parts.subject || "-"}`,
    ].join("|");
}
const defaultFormatter = (value) => Number.isInteger(value) ? String(value) : value.toFixed(2);
/** A formatter honouring an attribute's unit and declared decimal places. */
export function attributeFormatter(unit, decimals) {
    return (value) => {
        const text = decimals === null ? defaultFormatter(value) : value.toFixed(decimals);
        return unit ? `${text} ${unit}` : text;
    };
}
/** "3 min", "1 h 5 min" — durations as a woken person would read them. */
export function formatDuration(seconds) {
    const total = Math.max(0, Math.round(seconds));
    if (total < 60)
        return `${total} s`;
    const minutes = Math.round(total / 60);
    if (minutes < 60)
        return `${minutes} min`;
    const hours = Math.floor(minutes / 60);
    const rest = minutes % 60;
    return rest === 0 ? `${hours} h` : `${hours} h ${rest} min`;
}
export const ALERT_KIND_LABELS = {
    [FermentationAlertKindEnum.NODE_OFFLINE]: "Node offline",
    [FermentationAlertKindEnum.INPUT_MISSING]: "Input missing",
    [FermentationAlertKindEnum.DEVICE_FAULT]: "Device fault",
    [FermentationAlertKindEnum.PROCESS_LIMIT]: "Process limit",
};
export const ALERT_KIND_DESCRIPTIONS = {
    [FermentationAlertKindEnum.NODE_OFFLINE]: "The BrewControl node stopped talking to BrewBase, or stopped sending data while still connected.",
    [FermentationAlertKindEnum.INPUT_MISSING]: "A critical input stopped reporting a readable value — no samples at all, or the node reporting it could not read them.",
    [FermentationAlertKindEnum.DEVICE_FAULT]: "A device on the node reports an error.",
    [FermentationAlertKindEnum.PROCESS_LIMIT]: "A measured value sat outside its band, either an absolute range or a distance from its setpoint.",
};
/** One line naming what happened and where, used for SMS and for the call. */
export function alertHeadline(alert) {
    // A single-tank group is usually named after its tank, and "CF66 / CF66"
    // spends characters of an SMS saying one thing twice.
    const where = alert.fermentationName && alert.fermentationName !== alert.groupName
        ? `${alert.groupName} / ${alert.fermentationName}`
        : alert.groupName;
    return `${ALERT_KIND_LABELS[alert.kind]} on ${where}: ${alert.detail}`;
}
// =============================================================================
// VALIDATION
// =============================================================================
const numericish = z.number().finite();
export const alertRuleInputSchema = z
    .object({
    kind: z.nativeEnum(FermentationAlertKindEnum),
    enabled: z.boolean().default(true),
    durationS: z.number().int().min(0).max(86_400).default(DEFAULT_ALERT_DURATION_S),
    attributeId: z.number().int().nullable().default(null),
    /** The watched set, for input-missing. Empty for every other kind. */
    attributeIds: z.array(z.number().int()).default([]),
    limitMode: z.nativeEnum(FermentationAlertLimitModeEnum).nullable().default(null),
    minValue: numericish.nullable().default(null),
    maxValue: numericish.nullable().default(null),
    deviationBelow: numericish.nonnegative().nullable().default(null),
    deviationAbove: numericish.nonnegative().nullable().default(null),
    setpointAttributeId: z.number().int().nullable().default(null),
    displayOrder: z.number().int().default(0),
})
    // Mirrors the table's check constraints. The database is the boundary, but a
    // rule that cannot fire is a configuration mistake worth naming in the UI
    // rather than surfacing as a constraint violation.
    .superRefine((rule, ctx) => {
    const multi = isMultiSignalRule(rule.kind);
    const banded = rule.kind === FermentationAlertKindEnum.PROCESS_LIMIT;
    // A list rule needs at least one member; a band rule needs its one signal;
    // a node rule needs neither and must not carry either.
    if (multi && rule.attributeIds.length === 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attributeIds"],
            message: "Add at least one signal to watch.",
        });
    }
    if (!multi && rule.attributeIds.length > 0) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attributeIds"],
            message: "Only a missing-input alert watches a list of signals.",
        });
    }
    if (multi && new Set(rule.attributeIds).size !== rule.attributeIds.length) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attributeIds"],
            message: "The same signal is listed twice.",
        });
    }
    if (banded && rule.attributeId === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attributeId"],
            message: "This alert needs a signal to watch.",
        });
    }
    if (!banded && rule.attributeId !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["attributeId"],
            message: "This alert does not watch a single signal.",
        });
    }
    if (rule.kind !== FermentationAlertKindEnum.PROCESS_LIMIT) {
        if (rule.limitMode !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["limitMode"],
                message: "Only a process limit has a band.",
            });
        }
        return;
    }
    if (rule.limitMode === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["limitMode"],
            message: "Choose an absolute range or a deviation from setpoint.",
        });
        return;
    }
    if (rule.limitMode === FermentationAlertLimitModeEnum.ABSOLUTE) {
        if (rule.minValue === null && rule.maxValue === null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["minValue"],
                message: "Set at least one edge of the range.",
            });
        }
        if (rule.minValue !== null &&
            rule.maxValue !== null &&
            rule.minValue > rule.maxValue) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["maxValue"],
                message: "The maximum must not be below the minimum.",
            });
        }
        if (rule.deviationBelow !== null || rule.deviationAbove !== null) {
            ctx.addIssue({
                code: z.ZodIssueCode.custom,
                path: ["deviationBelow"],
                message: "An absolute range does not take a deviation.",
            });
        }
        return;
    }
    if (rule.deviationBelow === null && rule.deviationAbove === null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["deviationBelow"],
            message: "Set at least one side of the allowed deviation.",
        });
    }
    if (rule.minValue !== null || rule.maxValue !== null) {
        ctx.addIssue({
            code: z.ZodIssueCode.custom,
            path: ["minValue"],
            message: "A deviation band does not take an absolute range.",
        });
    }
});
/**
 * E.164: a leading `+`, a country code, then digits. Deliberately strict —
 * a number stored in a local format is a call that silently never connects.
 */
export const phoneE164Schema = z
    .string()
    .trim()
    .regex(/^\+[1-9]\d{6,14}$/, "Use international format, e.g. +31612345678");
//# sourceMappingURL=fermentation-alerts.js.map